import React, { useState } from "react";
import { createOrder } from "../services/lunchBuddy";
import { getTodayDateString } from "../lib/date";

interface Props {
  userEmail: string;
  displayName?: string;
  onOrderCreated: (order: any) => void;
  onCancel: () => void;
}

export default function CreateOrderPage({ userEmail, displayName, onOrderCreated, onCancel }: Props) {
  const [creating, setCreating] = useState(false);
  const [restaurant, setRestaurant] = useState("");
  const [deadline, setDeadline] = useState(60); // Default 60 minutes
  const [notes, setNotes] = useState("");
  const [link, setLink] = useState("");

  const today = getTodayDateString();

  const onCreateOrder = async () => {
    if (!restaurant.trim()) return;
    setCreating(true);
    try {
      const created = await createOrder({
        createdBy: userEmail,
        restaurant: restaurant.trim(),
        deadline,
        date: today,
        link: link.trim() || undefined,
        notes: notes.trim() || undefined,
        authorName: (displayName || '').trim() || undefined
      });
      onOrderCreated(created);
    } catch (error) {
      console.error("Error creating order:", error);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <header className="mb-8 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 mb-4">
              <span className="text-5xl">🍜</span>
            </div>
            <h1 className="mb-2 text-4xl font-bold tracking-tight text-white">
              Tạo phiên đặt đồ ăn
            </h1>
            <p className="text-lg text-slate-300">
              Tạo order mới để mọi người cùng tham gia
            </p>
            <p className="text-sm text-slate-400 mt-2">
              Tạo bởi: <span className="font-semibold text-emerald-400">{displayName || userEmail}</span>
            </p>
          </div>
        </header>

        {/* Main Form Card */}
        <div className="rounded-2xl border border-slate-700/40 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 shadow-2xl backdrop-blur-sm">
          <div className="space-y-6">
            {/* Restaurant Name */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                <span className="text-2xl">🏪</span>
                Tên cửa hàng
              </label>
              <input
                placeholder="Ví dụ: Cơm tấm Sài Gòn, KFC, Lotteria..."
                value={restaurant}
                onChange={e => setRestaurant(e.target.value)}
                className="w-full rounded-xl border border-slate-600 bg-slate-900/80 backdrop-blur-sm px-4 py-4 text-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Deadline */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                <span className="text-2xl">⏰</span>
                Thời gian order (phút)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={deadline}
                  onChange={e => setDeadline(parseInt(e.target.value))}
                  className="w-full rounded-xl border border-slate-600 bg-slate-900/80 backdrop-blur-sm px-4 py-4 text-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  <option value={15}>15 phút</option>
                  <option value={30}>30 phút</option>
                  <option value={45}>45 phút</option>
                  <option value={60}>1 giờ</option>
                  <option value={90}>1.5 giờ</option>
                  <option value={120}>2 giờ</option>
                  <option value={180}>3 giờ</option>
                </select>
                <input
                  type="number"
                  min="5"
                  max="480"
                  step="5"
                  value={deadline}
                  onChange={e => setDeadline(parseInt(e.target.value) || 60)}
                  className="w-full rounded-xl border border-slate-600 bg-slate-900/80 backdrop-blur-sm px-4 py-4 text-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  placeholder="Phút"
                />
              </div>
              <p className="mt-2 text-sm text-slate-400">
                Thời gian mọi người có để chọn món (tính từ lúc tạo order)
              </p>
            </div>

            {/* Restaurant Link */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                <span className="text-2xl">🔗</span>
                Link quán ăn (tùy chọn)
              </label>
              <input
                type="url"
                placeholder="https://shopee.com/shop123 hoặc link menu, fanpage..."
                value={link}
                onChange={e => setLink(e.target.value)}
                className="w-full rounded-xl border border-slate-600 bg-slate-900/80 backdrop-blur-sm px-4 py-4 text-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
              <p className="mt-2 text-sm text-slate-400">
                Link menu, fanpage, hoặc trang đặt hàng để team dễ dàng tham khảo
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                <span className="text-2xl">📝</span>
                Ghi chú (tùy chọn)
              </label>
              <textarea
                placeholder="Thêm thông tin về menu, giá cả, phương thức thanh toán..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={4}
                className="w-full rounded-xl border border-slate-600 bg-slate-900/80 backdrop-blur-sm px-4 py-4 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Preview Card */}
            {restaurant.trim() && (
              <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 p-6">
                <h4 className="mb-4 flex items-center gap-2 text-lg font-bold text-emerald-300">
                  <span className="text-xl">👀</span>
                  Xem trước
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">🏪</span>
                    <span className="text-white font-semibold">{restaurant}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">⏰</span>
                    <span className="text-slate-300">
                      {deadline >= 60 
                        ? `${Math.floor(deadline / 60)}h ${deadline % 60}m`
                        : `${deadline}m`} để chọn món
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">👤</span>
                    <span className="text-slate-300">Tạo bởi {displayName || userEmail}</span>
                  </div>
                  {link.trim() && (
                    <div className="flex gap-3">
                      <span className="text-lg">🔗</span>
                      <a 
                        href={link.startsWith('http') ? link : `https://${link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline break-all"
                      >
                        {link}
                      </a>
                    </div>
                  )}
                  {notes.trim() && (
                    <div className="flex gap-3">
                      <span className="text-lg">📝</span>
                      <span className="text-slate-300 flex-1">{notes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex gap-4">
            <button
              onClick={onCancel}
              className="flex-1 rounded-xl border border-slate-600 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white font-bold text-lg py-4 px-6 transition-all duration-300"
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl">❌</span>
                Hủy
              </div>
            </button>
            
            <button
              onClick={onCreateOrder}
              disabled={creating || !restaurant.trim()}
              className={`flex-1 rounded-xl font-bold text-lg py-4 px-6 transition-all duration-300 ${
                creating || !restaurant.trim() 
                  ? "bg-slate-700 text-slate-400 cursor-not-allowed" 
                  : "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg shadow-orange-500/25 hover:scale-105 active:scale-95"
              }`}
            >
              <div className="flex items-center justify-center gap-3">
                {creating ? (
                  <>
                    <div className="w-6 h-6 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"></div>
                    Đang tạo phiên...
                  </>
                ) : (
                  <>
                    <span className="text-2xl">🚀</span>
                    Tạo phiên đặt đồ ăn
                  </>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-8 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 p-6">
          <h4 className="mb-4 flex items-center gap-2 text-lg font-bold text-blue-300">
            <span className="text-xl">💡</span>
            Gợi ý
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex gap-3">
              <span>🕐</span>
              <span className="text-slate-300">Đặt deadline trước giờ ăn trưa khoảng 30-60 phút</span>
            </div>
            <div className="flex gap-3">
              <span>💰</span>
              <span className="text-slate-300">Ghi rõ phương thức thanh toán trong ghi chú</span>
            </div>
            <div className="flex gap-3">
              <span>📱</span>
              <span className="text-slate-300">Chia sẻ link menu để mọi người dễ chọn</span>
            </div>
            <div className="flex gap-3">
              <span>🚚</span>
              <span className="text-slate-300">Xác nhận địa chỉ giao hàng trước khi đặt</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
